// skipcq: JS-W1028
import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';

import mapFirebaseAuthError from '../../utils/mapFirebaseAuthError';
import { registerEmailPasswordUser } from '../../utils/firebase';
import {
  claimBreweryFormSchema,
  claimBreweryInitialValues,
} from './claimBreweryFormSchema';

export default function LoginClaimPane({ onSignupSuccessNavigate }) {
  const [claimError, setClaimError] = useState('');

  const formik = useFormik({
    initialValues: claimBreweryInitialValues,
    validationSchema: claimBreweryFormSchema,
    onSubmit: async (values, helpers) => {
      setClaimError('');
      try {
        const { verificationEmailError } = await registerEmailPasswordUser({
          email: values.claimEmail,
          password: values.claimPassword,
          firstName: values.firstName,
          lastName: values.lastName,
          breweryRole: values.breweryRole,
        });
        if (typeof onSignupSuccessNavigate === 'function') {
          onSignupSuccessNavigate({ verificationEmailError });
        }
      } catch (error) {
        setClaimError(mapFirebaseAuthError(error));
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  const gridFields = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: '1fr 1fr',
        },
        columnGap: 2,
      }}
    >
      {claimError ? (
        <Box sx={{ gridColumn: '1 / -1' }}>
          <Alert severity="error">{claimError}</Alert>
        </Box>
      ) : null}

      <TextFieldClaimRow formik={formik} name="firstName" label="First name" />
      <TextFieldClaimRow formik={formik} name="lastName" label="Last name" />

      <Stack spacing={1} sx={{ gridColumn: '1 / -1', width: '100%' }}>
        <TextFieldClaimRow formik={formik} name="breweryRole" label="Role at brewery" />
        <TextFieldClaimRow
          formik={formik}
          name="claimEmail"
          label="Email"
          type="email"
          autoComplete="email"
        />
        <TextFieldClaimRow
          formik={formik}
          name="claimPassword"
          label="Password"
          type="password"
          autoComplete="new-password"
        />
      </Stack>

      <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
        <Button
          type="submit"
          variant="contained"
          color="secondary"
          size="large"
          fullWidth
          disabled={formik.isSubmitting}
        >
          Claim Brewery
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: { xs: 'stretch', md: 'center' },
        py: { xs: 3, md: 4 },
        px: { xs: 2, sm: 4 },
      }}
    >
      <Paper
        component="form"
        noValidate
        elevation={0}
        onSubmit={formik.handleSubmit}
        sx={{
          width: '100%',
          maxWidth: 440,
          p: 3,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" align="center" gutterBottom>
          Claim your brewery!
        </Typography>
        <TypographyIntro />
        <TypographySub />
        {gridFields}
      </Paper>
    </Box>
  );
}

function TypographyIntro() {
  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.default',
      }}
    >
      <Typography variant="body2" align="center">
        {"Manage your brewery on I’d Tap That in a few easy steps."}
      </Typography>
    </Box>
  );
}

function TypographySub() {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      sx={{ mb: 2 }}
    >
      {
        'Create an email and password for logging in to manage your brewery once verified.'
      }
    </Typography>
  );
}

function TextFieldClaimRow({ formik, name, label, type = 'text', autoComplete }) {
  const touched = formik.touched[name];
  const errorMessage = formik.errors[name];
  return (
    <TextField
      name={name}
      label={label}
      type={type}
      autoComplete={autoComplete}
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={Boolean(touched) && Boolean(errorMessage)}
      helperText={
        touched && errorMessage ? errorMessage : ' '
      }
      fullWidth
      margin="dense"
    />
  );
}
